import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatDuree } from '../utils/formatDuree';

function ModalStatistiques({ operateur, onClose }) {
  const [stats, setStats] = useState({
    nbOperations: 0,
    nbHeures: 0,
    chiffreAffaire: 0
  });
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  useEffect(() => {
    // Définir par défaut la semaine précédente
    const aujourdhui = new Date();
    const finSemaine = new Date(aujourdhui);
    finSemaine.setDate(aujourdhui.getDate() - aujourdhui.getDay() + 7);
    
    const debutSemaine = new Date(finSemaine);
    debutSemaine.setDate(finSemaine.getDate() - 6);
    
    setDateDebut(debutSemaine.toISOString().split('T')[0]);
    setDateFin(finSemaine.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateDebut && dateFin) {
      loadStats();
    }
  }, [dateDebut, dateFin]);

  const loadStats = async () => {
    try {
      // Récupérer toutes les opérations de cet opérateur sur des cartes TERMINÉES
      const { data: suivis, error } = await supabase
        .from('suivi_operations')
        .select(`
          *,
          operations (
            temps_realisation,
            cout_fixe,
            nom
          ),
          cartes_production!inner (
            statut,
            date_fin,
            updated_at
          )
        `)
        .eq('operateur_id', operateur.id)
        .eq('cartes_production.statut', 'terminee');

      if (error) {
        console.error('❌ Erreur chargement stats:', error);
        return;
      }

      // Filtrer par date (date de fin de la carte)
      const suivisDansPeriode = suivis?.filter(suivi => {
        const carte = suivi.cartes_production;
        if (!carte) return false;
        
        // Utiliser la date de fin de la carte (ou updated_at si date_fin n'existe pas)
        const dateCarte = carte.date_fin || carte.updated_at;
        if (!dateCarte) return false;
        
        const dateFinCarte = new Date(dateCarte);
        const debut = new Date(dateDebut);
        const fin = new Date(dateFin);
        fin.setHours(23, 59, 59, 999);
        
        return dateFinCarte >= debut && dateFinCarte <= fin;
      }) || [];

      console.log('📊 Opérations sur cartes terminées dans la période:', suivisDansPeriode);

      // Calculer les statistiques
      const nbOperations = suivisDansPeriode.length;
      
      // Calculer le nombre d'heures
      // duree_reelle est en minutes, temps_realisation est en secondes
      const totalSecondes = suivisDansPeriode.reduce((sum, suivi) => {
        if (suivi.duree_reelle) {
          // duree_reelle est en minutes, convertir en secondes
          return sum + (suivi.duree_reelle * 60);
        }
        return sum + (suivi.operations?.temps_realisation || 0);
      }, 0);
      const nbHeuresFormatted = formatDuree(totalSecondes);
      
      // Calculer le chiffre d'affaires (cout_fixe par opération)
      const chiffreAffaire = suivisDansPeriode.reduce((sum, suivi) => {
        return sum + (parseFloat(suivi.operations?.cout_fixe) || 0);
      }, 0);

      setStats({
        nbOperations,
        nbHeures: nbHeuresFormatted,
        chiffreAffaire: chiffreAffaire.toFixed(2)
      });
    } catch (error) {
      console.error('❌ Erreur calcul stats:', error);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        width: '800px',
        maxWidth: '95%',
        border: '2px solid #1aabf9',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '30px 40px',
          borderBottom: '2px solid #e0e5ea',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ 
              color: '#212529',
              margin: 0,
              marginBottom: '5px',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              📊 Statistiques - {operateur.prenom} {operateur.nom}
            </h2>
            <div style={{ color: '#6c757d', fontSize: '14px' }}>
              Code-barre: <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{operateur.code_barre}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: '#6c757d',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Fermer
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '40px' }}>
          {/* Filtres de date */}
          <div style={{
            display: 'flex',
            gap: '20px',
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '10px',
            alignItems: 'end'
          }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                color: '#495057',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Date de début
              </label>
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                style={{
                  width: '80%',
                  padding: '10px 14px',
                  backgroundColor: '#ffffff',
                  color: '#212529',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                color: '#495057',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Date de fin
              </label>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                style={{
                  width: '80%',
                  padding: '10px 14px',
                  backgroundColor: '#ffffff',
                  color: '#212529',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <button
              onClick={loadStats}
              style={{
                padding: '10px 24px',
                backgroundColor: '#1aabf9',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}
            >
              Actualiser
            </button>
          </div>

          {/* Cartes statistiques */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: operateur.type_contrat === 'sous_traitant' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
            gap: '20px'
          }}>
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '30px',
              borderRadius: '15px',
              border: '2px solid #1aabf9',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '42px',
                fontWeight: '700',
                color: '#1aabf9',
                marginBottom: '10px'
              }}>
                {stats.nbOperations}
              </div>
              <div style={{
                color: '#495057',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Opérations effectuées
              </div>
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              padding: '30px',
              borderRadius: '15px',
              border: '2px solid #ffc107',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '42px',
                fontWeight: '700',
                color: '#ff8c00',
                marginBottom: '10px'
              }}>
                {stats.nbHeures}
              </div>
              <div style={{
                color: '#495057',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Heures travaillées
              </div>
            </div>

            {/* Chiffre d'affaires - Uniquement pour sous-traitants */}
            {operateur.type_contrat === 'sous_traitant' && (
              <div style={{
                backgroundColor: '#d4edda',
                padding: '30px',
                borderRadius: '15px',
                border: '2px solid #28a745',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: '700',
                  color: '#28a745',
                  marginBottom: '10px'
                }}>
                  {stats.chiffreAffaire}€
                </div>
                <div style={{
                  color: '#495057',
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Chiffre d'affaires
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalStatistiques;